import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CharacterBenefits } from '../../hooks/useCharacterBenefits';
import { generateCombatTips, type TipContext } from '@character-creator/data';
import { usePlaySessionStore } from '../../stores/playSessionStore';

interface CheatSheetProps {
  benefits: CharacterBenefits;
}

/** Derive a stable ID from a tip title */
function slugifyTipId(title: string): string {
  return `auto-${title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;
}

interface MergedTip {
  id: string;
  icon: string;
  title: string;
  description: string;
  isCustom: boolean;
}

/**
 * Cheat Sheet — interactive tips & combat notes
 * Replaces the old stateless CombatTips component.
 */
export function CheatSheet({ benefits }: CheatSheetProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const session = usePlaySessionStore((s) => s.activeSession);
  const cheatSheet = session?.cheatSheet;
  const dismissCheatSheetTip = usePlaySessionStore((s) => s.dismissCheatSheetTip);
  const restoreCheatSheetTip = usePlaySessionStore((s) => s.restoreCheatSheetTip);
  const addCustomCheatSheetTip = usePlaySessionStore((s) => s.addCustomCheatSheetTip);
  const removeCustomCheatSheetTip = usePlaySessionStore((s) => s.removeCustomCheatSheetTip);
  const reorderCheatSheetTip = usePlaySessionStore((s) => s.reorderCheatSheetTip);
  const setCombatNotes = usePlaySessionStore((s) => s.setCombatNotes);
  const updateCheatSheetTipOrder = usePlaySessionStore((s) => s.updateCheatSheetTipOrder);

  // Generate auto tips from character benefits
  const autoTips = useMemo(() => {
    const context: TipContext = {
      classNames: benefits.classLevels.map((c) => c.className),
      featureIds: benefits.classFeatures.map((f) => f.id),
      hasSpellcasting: benefits.spellcasting !== null,
      level: benefits.characterLevel,
      raceName: benefits.raceName ?? undefined,
      subraceName: benefits.subraceName ?? undefined,
    };
    return generateCombatTips(context);
  }, [benefits]);

  // Build auto tip map: id -> tip data
  const autoTipMap = useMemo(() => {
    const map = new Map<string, { icon: string; title: string; description: string }>();
    for (const tip of autoTips) {
      map.set(slugifyTipId(tip.title), { icon: tip.icon, title: tip.title, description: tip.description });
    }
    return map;
  }, [autoTips]);

  // Build custom tip map
  const customTipMap = useMemo(() => {
    const map = new Map<string, { icon: string; title: string; description: string }>();
    if (cheatSheet?.customTips) {
      for (const tip of cheatSheet.customTips) {
        map.set(tip.id, { icon: tip.icon, title: tip.title, description: tip.description });
      }
    }
    return map;
  }, [cheatSheet?.customTips]);

  // Merge-on-render: compute the merged tip order
  const mergedTipOrder = useMemo(() => {
    const storedOrder = cheatSheet?.tipOrder ?? [];
    const allAutoIds = [...autoTipMap.keys()];
    const allCustomIds = [...customTipMap.keys()];
    const validIds = new Set([...allAutoIds, ...allCustomIds]);

    // Start with stored order, filtering out stale IDs
    const merged = storedOrder.filter((id) => validIds.has(id));

    // Append any new auto tips not yet in stored order
    const mergedSet = new Set(merged);
    for (const id of allAutoIds) {
      if (!mergedSet.has(id)) {
        merged.push(id);
      }
    }
    // Append any new custom tips not yet in stored order
    for (const id of allCustomIds) {
      if (!mergedSet.has(id)) {
        merged.push(id);
      }
    }

    return merged;
  }, [autoTipMap, customTipMap, cheatSheet?.tipOrder]);

  // Persist merged order if it differs from stored
  const prevMergedRef = useRef<string[]>([]);
  useEffect(() => {
    const storedOrder = cheatSheet?.tipOrder ?? [];
    const orderChanged = mergedTipOrder.length !== storedOrder.length ||
      mergedTipOrder.some((id, i) => storedOrder[i] !== id);
    const prevChanged = mergedTipOrder.length !== prevMergedRef.current.length ||
      mergedTipOrder.some((id, i) => prevMergedRef.current[i] !== id);
    if (orderChanged && prevChanged) {
      updateCheatSheetTipOrder(mergedTipOrder);
    }
    prevMergedRef.current = mergedTipOrder;
  }, [mergedTipOrder, cheatSheet?.tipOrder, updateCheatSheetTipOrder]);

  // Build the visible tip list
  const dismissedSet = useMemo(
    () => new Set(cheatSheet?.dismissedTipIds ?? []),
    [cheatSheet?.dismissedTipIds],
  );

  const allTips: MergedTip[] = useMemo(() => {
    return mergedTipOrder.map((id) => {
      const auto = autoTipMap.get(id);
      if (auto) return { id, ...auto, isCustom: false };
      const custom = customTipMap.get(id);
      if (custom) return { id, ...custom, isCustom: true };
      return null;
    }).filter((t): t is MergedTip => t !== null);
  }, [mergedTipOrder, autoTipMap, customTipMap]);

  const visibleTips = useMemo(
    () => (editMode ? allTips : allTips.filter((t) => !dismissedSet.has(t.id))),
    [allTips, dismissedSet, editMode],
  );

  const handleAddCustomTip = useCallback(() => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    addCustomCheatSheetTip(trimmed, newDescription.trim());
    setNewTitle('');
    setNewDescription('');
  }, [newTitle, newDescription, addCustomCheatSheetTip]);

  // Show top 3 when collapsed (non-edit), all when expanded or in edit mode
  const displayTips = isExpanded || editMode ? visibleTips : visibleTips.slice(0, 3);
  const hasMore = visibleTips.length > 3;

  if (allTips.length === 0 && !editMode) {
    return null;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 font-display text-xs text-accent-gold uppercase tracking-wider flex items-center gap-2 hover:text-accent-gold/80 transition-colors"
        >
          <span className="text-base">💡</span>
          Cheat Sheet
          <div className="flex-1 h-px bg-background-elevated" />
          {hasMore && !editMode && (
            <span className="text-[10px] text-text-muted font-normal lowercase">
              {isExpanded ? 'show less' : `+${visibleTips.length - 3} more`}
            </span>
          )}
          <span className="text-text-muted text-xs">
            {isExpanded ? '▼' : '▶'}
          </span>
        </button>
        <button
          onClick={() => setEditMode(!editMode)}
          className={`p-1 rounded transition-colors ${
            editMode
              ? 'text-accent-gold bg-accent-gold/10'
              : 'text-text-muted hover:text-text-primary'
          }`}
          title={editMode ? 'Done editing' : 'Customize'}
          aria-label={editMode ? 'Done editing cheat sheet' : 'Customize cheat sheet'}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>

      {/* Tips list */}
      <div className="space-y-2">
        {displayTips.map((tip, idx) => {
          const isDismissed = dismissedSet.has(tip.id);
          return (
            <div
              key={tip.id}
              className={`group p-3 bg-background-card/50 border border-background-elevated rounded-lg transition-opacity ${
                isDismissed ? 'opacity-40' : ''
              }`}
            >
              <div className="flex items-start gap-2">
                {/* Edit mode: reorder buttons */}
                {editMode && (
                  <div className="flex flex-col gap-0.5 mt-0.5">
                    <button
                      onClick={() => reorderCheatSheetTip(tip.id, 'up')}
                      disabled={idx === 0}
                      className="text-xs text-text-muted hover:text-text-primary disabled:opacity-30
                        disabled:cursor-not-allowed transition-colors w-4 h-3.5 flex items-center justify-center"
                      aria-label={`Move ${tip.title} up`}
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => reorderCheatSheetTip(tip.id, 'down')}
                      disabled={idx === displayTips.length - 1}
                      className="text-xs text-text-muted hover:text-text-primary disabled:opacity-30
                        disabled:cursor-not-allowed transition-colors w-4 h-3.5 flex items-center justify-center"
                      aria-label={`Move ${tip.title} down`}
                    >
                      ▼
                    </button>
                  </div>
                )}

                <span className="text-base flex-shrink-0">{tip.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`font-display text-sm ${isDismissed ? 'text-text-muted line-through' : 'text-text-primary'} mb-0.5`}>
                      {tip.title}
                    </span>
                    {tip.isCustom && (
                      <span className="text-[9px] text-text-muted bg-background-elevated px-1 py-0.5 rounded">
                        custom
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-muted leading-relaxed">
                    {tip.description}
                  </p>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {editMode ? (
                    <>
                      {/* Dismiss/restore toggle for auto tips */}
                      {!tip.isCustom && (
                        <button
                          onClick={() => isDismissed ? restoreCheatSheetTip(tip.id) : dismissCheatSheetTip(tip.id)}
                          className={`p-1 transition-colors ${
                            isDismissed
                              ? 'text-text-muted hover:text-text-primary'
                              : 'text-accent-gold hover:text-accent-gold/80'
                          }`}
                          title={isDismissed ? 'Show' : 'Hide'}
                          aria-label={isDismissed ? `Restore ${tip.title}` : `Dismiss ${tip.title}`}
                        >
                          {isDismissed ? (
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      )}
                      {/* Delete button for custom tips */}
                      {tip.isCustom && (
                        <button
                          onClick={() => removeCustomCheatSheetTip(tip.id)}
                          className="p-1 text-text-muted hover:text-error transition-colors"
                          title="Delete"
                          aria-label={`Delete ${tip.title}`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </>
                  ) : (
                    /* Dismiss button (non-edit mode) */
                    <button
                      onClick={() => dismissCheatSheetTip(tip.id)}
                      className="p-1 text-text-muted hover:text-text-primary transition-colors opacity-0 group-hover:opacity-100"
                      title="Dismiss"
                      aria-label={`Dismiss ${tip.title}`}
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add custom tip form (edit mode only) */}
      {editMode && (
        <div className="mt-3 p-3 bg-background-card border border-accent-bronze/30 rounded-lg space-y-2">
          <div className="font-display text-[10px] text-accent-gold uppercase tracking-wider">Add Custom Tip</div>
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Tip title..."
            className="w-full bg-background-elevated border border-background-elevated rounded
              px-2 py-1.5 text-sm text-text-primary
              focus:outline-none focus:border-accent-bronze/50 placeholder:text-text-muted"
          />
          <input
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustomTip(); }}
            placeholder="Description (optional)..."
            className="w-full bg-background-elevated border border-background-elevated rounded
              px-2 py-1.5 text-sm text-text-primary
              focus:outline-none focus:border-accent-bronze/50 placeholder:text-text-muted"
          />
          <button
            onClick={handleAddCustomTip}
            disabled={!newTitle.trim()}
            className="px-3 py-1.5 text-xs font-display uppercase tracking-wider
              bg-accent-bronze/20 text-accent-gold border border-accent-bronze/30 rounded
              hover:bg-accent-bronze/30 transition-colors
              disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Add Tip
          </button>
        </div>
      )}

      {/* Combat Notes — always visible when expanded */}
      {(isExpanded || editMode) && (
        <div className="mt-3">
          <div className="font-display text-[10px] text-accent-gold uppercase tracking-wider mb-1.5 flex items-center gap-2">
            Combat Notes
            <div className="flex-1 h-px bg-background-elevated" />
          </div>
          <textarea
            value={cheatSheet?.combatNotes ?? ''}
            onChange={(e) => setCombatNotes(e.target.value)}
            placeholder="Jot quick combat notes here..."
            rows={3}
            className="w-full bg-background-card border border-background-elevated rounded-lg
              px-3 py-2 text-sm text-text-primary resize-y
              focus:outline-none focus:border-accent-bronze/50 placeholder:text-text-muted"
          />
        </div>
      )}
    </div>
  );
}
